import { rememberSize, restoreSize, syncSizeForm, SIZE_MEMORY_EVENT } from '@theme/size-memory';

const COMPONENT_SELECTOR = 'product-card-variant-selector';
const OPTION_SELECTOR = `${COMPONENT_SELECTOR} input[type="radio"][data-variant-id]`;
const CHIP_SELECTOR = `${COMPONENT_SELECTOR} .product-card-variant-selector__chip`;
const ADD_LABEL_SELECTOR = '[data-add-to-cart-custom-label]';
const INLINE_PRICE_SELECTOR = '[data-product-title-inline-price]';
const PRODUCT_FORM_SELECTOR = 'product-form-component';
const ADD_BUTTON_SELECTOR = 'product-form-component [ref="addToCartButton"], product-form-component button[name="add"]';

function getCheckedInput(card) {
  const checkedInput = card.querySelector(`${COMPONENT_SELECTOR} input[type="radio"][data-variant-id]:checked`);
  return checkedInput instanceof HTMLInputElement ? checkedInput : null;
}

function syncForm(form, input) {
  form.dataset.selectedSize = input.value;
  const purchaseRestricted = form.dataset.purchaseRestricted === 'true';
  const canPurchase = input.dataset.variantAvailable === 'true' && !purchaseRestricted;
  const formVariantInput = form.querySelector('input[name="id"]');
  if (formVariantInput instanceof HTMLInputElement) {
    const variantId = input.dataset.variantId || '';
    formVariantInput.value = variantId;
    formVariantInput.setAttribute('value', variantId);
    formVariantInput.disabled = !canPurchase;
  }

  const addButton = form.querySelector('[ref="addToCartButton"]');
  if (addButton instanceof HTMLButtonElement) {
    addButton.disabled = !canPurchase;
    addButton.setAttribute('aria-disabled', String(!canPurchase));
  }
  syncSizeForm(form);
}

function syncProductForm(input) {
  const card = input.closest('product-card');
  if (!card) return;

  const targetForms = Array.from(card.querySelectorAll(PRODUCT_FORM_SELECTOR)).filter(
    (form) => form instanceof HTMLElement
  );

  if (!targetForms.length) return;

  targetForms.forEach((targetForm) => syncForm(targetForm, input));

  const price = input.dataset.variantPrice || '';
  card.querySelectorAll(ADD_LABEL_SELECTOR).forEach((label) => {
    if (!(label instanceof HTMLElement)) return;
    const baseLabel = label.dataset.baseLabel || label.textContent?.trim() || '';
    const showPrice = label.dataset.showPrice === 'true';
    label.textContent = showPrice && price ? `${baseLabel} ${price}` : baseLabel;
  });

  card.querySelectorAll(INLINE_PRICE_SELECTOR).forEach((priceElement) => {
    if (priceElement instanceof HTMLElement && price) {
      priceElement.textContent = price;
    }
  });
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches(OPTION_SELECTOR)) return;

  const picker = target.closest(COMPONENT_SELECTOR);
  if (!event.detail?.sizeMemoryRestore) rememberSize(picker.dataset.sizeGroup, target.value, picker);

  syncProductForm(target);
});

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const addButton = target.closest(ADD_BUTTON_SELECTOR);
    if (addButton instanceof HTMLElement) {
      const card = addButton.closest('product-card');
      const productForm = addButton.closest(PRODUCT_FORM_SELECTOR);
      if (card && productForm instanceof HTMLElement) {
        const checkedInput = getCheckedInput(card);
        if (checkedInput) syncForm(productForm, checkedInput);

        // Restricted products (for example Coming Soon) must remain inert.
        // The capturing listener runs before the button's add-to-cart handler,
        // so stop here as an additional safeguard against visual cart feedback.
        if (productForm.dataset.purchaseRestricted === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
      }
    }

    const chip = target.closest(CHIP_SELECTOR);
    if (!(chip instanceof HTMLLabelElement)) return;

    const inputId = chip.getAttribute('for');
    if (!inputId) return;

    const input = document.getElementById(inputId);
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    event.preventDefault();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const productForm = form.closest(PRODUCT_FORM_SELECTOR);
    const card = form.closest('product-card');
    if (!(productForm instanceof HTMLElement) || !card) return;

    const checkedInput = getCheckedInput(card);
    if (checkedInput) syncForm(productForm, checkedInput);
  },
  true
);

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll(OPTION_SELECTOR).forEach((input) => {
    if (input instanceof HTMLInputElement && input.checked) {
      syncProductForm(input);
    }
  });
});

if (!customElements.get('product-card-variant-selector')) {
  customElements.define(
    'product-card-variant-selector',
    class extends HTMLElement {
      #onRememberedSizeChange = (event) => {
        if (event.detail.group === this.dataset.sizeGroup && event.detail.source !== this) this.restoreRememberedSize();
      };

      restoreRememberedSize() {
        restoreSize(this, (input) => {
          input.checked = true;
          input.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { sizeMemoryRestore: true } }));
        });
      }

      connectedCallback() {
        const checkedInput = this.querySelector('input[type="radio"][data-variant-id]:checked');
        if (checkedInput instanceof HTMLInputElement) {
          syncProductForm(checkedInput);
        }
        document.addEventListener(SIZE_MEMORY_EVENT, this.#onRememberedSizeChange);
        queueMicrotask(() => this.restoreRememberedSize());
      }

      disconnectedCallback() {
        document.removeEventListener(SIZE_MEMORY_EVENT, this.#onRememberedSizeChange);
      }
    }
  );
}
